import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  IconButton, 
  Avatar, 
  Chip, 
  Grid, 
  Card, 
  CardContent, 
  Badge, 
  Button, 
  Fab, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Rating,
  Tooltip
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HistoryIcon from '@mui/icons-material/History';
import TaskIcon from '@mui/icons-material/Task';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CoffeeIcon from '@mui/icons-material/Coffee';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupIcon from '@mui/icons-material/Group';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import HelpIcon from '@mui/icons-material/Help';
import CelebrationIcon from '@mui/icons-material/Celebration';
import MoodIcon from '@mui/icons-material/Mood';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useGlobalContext } from '../../GlobalProvider';
import { 
  getTasksByAgent, 
  getChatConversations, 
  chatWithAgentWorkforce,
  createConversation
} from '../../api';

const AgentChat = ({ agent, onClose }) => {
  const { appId } = useGlobalContext();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentTasks, setCurrentTasks] = useState([]);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [agentMood, setAgentMood] = useState('productive');
  const [workStatus, setWorkStatus] = useState('focused');
  const [dailyGoals, setDailyGoals] = useState([]);
  const [collaborationRequests, setCollaborationRequests] = useState([]);
  const [feedback, setFeedback] = useState({});
  const messagesEndRef = useRef(null);

  // Generate human-like conversation starters based on time and context
  const getConversationStarters = (agent) => {
    const hour = new Date().getHours();
    const dayOfWeek = new Date().getDay();
    
    const morningStarters = [
      `Good morning! How's your day starting?`,
      `Morning! What's on your agenda today?`,
      `Hey! Ready to tackle today's challenges?`,
      `Good morning! Any exciting projects you're working on?`
    ];
    
    const afternoonStarters = [
      `How's your afternoon going? Need any help?`,
      `Hey! How are those tasks coming along?`,
      `Afternoon check-in - everything on track?`,
      `How's the day progressing? Any blockers?`
    ];
    
    const eveningStarters = [
      `Wrapping up for the day? How did it go?`,
      `Evening! Any wins to celebrate today?`,
      `How was your day? Any highlights?`,
      `End of day check-in - how are you feeling?`
    ];
    
    const workStarters = [
      `What's your biggest challenge right now?`,
      `How can I support you today?`,
      `Any projects you'd like to discuss?`,
      `What's your priority for this week?`
    ];
    
    const personalStarters = [
      `How are you feeling today?`,
      `Any work-life balance concerns?`,
      `What would make your day better?`,
      `How's your energy level?`
    ];
    
    const collaborationStarters = [
      `Who should we collaborate with on this?`,
      `Any team members you'd like to loop in?`,
      `What resources do you need?`,
      `How can we improve our workflow?`
    ];

    let starters = [];
    
    if (hour < 12) {
      starters = [...morningStarters];
    } else if (hour < 17) {
      starters = [...afternoonStarters];
    } else {
      starters = [...eveningStarters];
    }
    
    starters = [...starters, ...workStarters, ...personalStarters, ...collaborationStarters];
    return starters.sort(() => Math.random() - 0.5).slice(0, 6);
  };

  // Generate daily goals and work patterns
  const generateDailyGoals = (agent) => {
    const goals = [
      { id: 1, title: 'Complete data analysis report', priority: 'high', progress: 75, deadline: 'Today 5 PM' },
      { id: 2, title: 'Review team feedback', priority: 'medium', progress: 30, deadline: 'Tomorrow' },
      { id: 3, title: 'Update documentation', priority: 'low', progress: 0, deadline: 'This week' },
      { id: 4, title: 'Prepare presentation', priority: 'high', progress: 90, deadline: 'Today 3 PM' }
    ];
    return goals;
  };

  // Generate collaboration requests
  const generateCollaborationRequests = () => {
    return [
      { id: 1, type: 'review', from: 'Marketing Team', message: 'Need your input on the new campaign data', priority: 'medium' },
      { id: 2, type: 'meeting', from: 'Product Manager', message: 'Can we sync on the Q4 roadmap?', priority: 'high' },
      { id: 3, type: 'feedback', from: 'Design Team', message: 'Your feedback on the new UI would be valuable', priority: 'low' }
    ];
  };

  // Fetch tasks from backend
  const fetchTasks = async () => {
    if (!agent?._id || !appId) return;
    
    try {
      setError(null);
      const response = await getTasksByAgent(agent._id, appId, { status: 'active', limit: 5 });
      setCurrentTasks(response.data.data || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
    }
  };

  // Fetch conversation history from backend
  const fetchConversationHistory = async () => {
    if (!agent?._id || !appId) return;
    
    try {
      setError(null);
      const response = await getChatConversations(agent._id, appId);
      setChatHistory(response.data.data || []);
    } catch (err) {
      console.error('Error fetching conversation history:', err);
      setError('Failed to load conversation history');
    }
  };

  // Create new conversation
  const createNewConversation = async () => {
    if (!agent?._id || !appId) return;
    
    try {
      const conversationData = {
        agentWorkforceId: agent._id,
        title: `Chat with ${agent.name}`,
        type: 'general'
      };
      
      const response = await createConversation(conversationData, appId);
      setCurrentConversationId(response.data.data._id);
      return response.data.data._id;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError('Failed to create conversation');
      return null;
    }
  };

  // Send message with backend integration
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !agent?._id || !appId) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Create conversation if it doesn't exist
      let conversationId = currentConversationId;
      if (!conversationId) {
        conversationId = await createNewConversation();
        if (!conversationId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Send message to backend
      const response = await chatWithAgentWorkforce(agent._id, [userMessage], appId, conversationId);
      
      const aiMessage = {
        role: 'assistant',
        content: response.data.reply || 'I understand your message. How can I help you further?',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Refresh conversation history
      await fetchConversationHistory();
      
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Load conversation
  const loadConversation = async (conversationId) => {
    try {
      setLoading(true);
      setError(null);
      
      // For now, we'll just set the conversation ID
      // In a full implementation, you'd fetch the conversation messages
      setCurrentConversationId(conversationId);
      setMessages([]);
      
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  // Initialize data when component mounts
  useEffect(() => {
    if (agent && appId) {
      setLoading(true);
      Promise.all([
        fetchTasks(),
        fetchConversationHistory()
      ]).finally(() => {
        setLoading(false);
      });
      
      setConversationStarters(getConversationStarters(agent));
      setDailyGoals(generateDailyGoals(agent));
      setCollaborationRequests(generateCollaborationRequests());
      
      // Add human-like welcome message based on time
      const hour = new Date().getHours();
      let welcomeMessage = '';
      
      if (hour < 12) {
        welcomeMessage = `Good morning! I'm ready to tackle today's challenges. How can I help you?`;
      } else if (hour < 17) {
        welcomeMessage = `Good afternoon! I'm in the middle of some interesting work. What's on your mind?`;
      } else {
        welcomeMessage = `Good evening! I'm wrapping up for the day. How can I assist you?`;
      }
      
      const welcomeMsg = {
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date().toISOString()
      };
      
      setMessages([welcomeMsg]);
    }
  }, [agent, appId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  // Get mood icon
  const getMoodIcon = (mood) => {
    switch (mood) {
      case 'productive': return <TrendingUpIcon />;
      case 'focused': return <WorkIcon />;
      case 'creative': return <LightbulbIcon />;
      case 'collaborative': return <GroupIcon />;
      case 'relaxed': return <CoffeeIcon />;
      default: return <MoodIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Panel - Employee Status & Work Context */}
        <Paper sx={{ width: 350, overflow: 'auto', borderRight: 1, borderColor: 'divider' }}>
          {/* Employee Status Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar src={agent.icon} alt={agent.name} sx={{ width: 60, height: 60 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">{agent.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {agent.category} Specialist
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {getMoodIcon(agentMood)}
                  <Typography variant="caption" color="text.secondary">
                    {agentMood.charAt(0).toUpperCase() + agentMood.slice(1)} • {workStatus}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Quick Status Update */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Available" color="success" size="small" />
              <Chip label="In Office" color="info" size="small" />
              <Chip label="Focused" color="primary" size="small" />
            </Box>
          </Box>

          {/* Work Context Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Today" />
              <Tab label="Work" />
              <Tab label="Team" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 2 }}>
            {activeTab === 0 && (
              <Box>
                {/* Daily Goals */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Today's Goals
                </Typography>
                <List dense>
                  {dailyGoals.map((goal) => (
                    <ListItem key={goal.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={goal.title}
                        secondary={`${goal.progress}% • Due: ${goal.deadline}`}
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                      <Chip 
                        label={goal.priority} 
                        size="small" 
                        color={getPriorityColor(goal.priority)}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Work Schedule */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                  Today's Schedule
                </Typography>
                <Timeline position="right" sx={{ p: 0 }}>
                  <TimelineItem>
                    <TimelineOppositeContent sx={{ fontSize: '0.75rem' }}>
                      9:00 AM
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="primary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2">Morning standup</Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineOppositeContent sx={{ fontSize: '0.75rem' }}>
                      11:00 AM
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="secondary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2">Data analysis review</Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineOppositeContent sx={{ fontSize: '0.75rem' }}>
                      2:00 PM
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2">Team collaboration</Typography>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                {/* Current Tasks */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Active Tasks ({currentTasks.length})
                </Typography>
                {currentTasks.length > 0 ? (
                  <List dense>
                    {currentTasks.map((task) => (
                      <ListItem key={task._id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={task.name}
                          secondary={task.description}
                          primaryTypographyProps={{ fontSize: '0.9rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        <Chip 
                          label={task.status} 
                          size="small" 
                          color={getStatusColor(task.status)}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No active tasks
                  </Typography>
                )}

                {/* Performance Metrics */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                  This Week's Performance
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Tasks Completed</Typography>
                    <Typography variant="body2" fontWeight="bold">12/15</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Success Rate</Typography>
                    <Typography variant="body2" fontWeight="bold">95%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Hours Worked</Typography>
                    <Typography variant="body2" fontWeight="bold">32/40</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                {/* Collaboration Requests */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Collaboration Requests
                </Typography>
                {collaborationRequests.length > 0 ? (
                  <List dense>
                    {collaborationRequests.map((request) => (
                      <ListItem key={request.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={request.from}
                          secondary={request.message}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 'medium' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                        <Chip 
                          label={request.priority} 
                          size="small" 
                          color={getPriorityColor(request.priority)}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No pending requests
                  </Typography>
                )}

                {/* Team Availability */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                  Team Status
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>M</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Marketing Team"
                      secondary="Available for collaboration"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'warning.main' }}>P</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Product Manager"
                      secondary="In a meeting until 3 PM"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItem>
                </List>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Center Panel - Main Chat */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Messages Area */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  {message.role === 'assistant' && (
                    <Avatar src={agent.icon} alt={agent.name} sx={{ width: 32, height: 32 }} />
                  )}
                  
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                      color: message.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      maxWidth: '100%'
                    }}
                  >
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      {message.content}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        mt: 0.5,
                        opacity: 0.7 
                      }}
                    >
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Paper>
                  
                  {message.role === 'user' && (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      You
                    </Avatar>
                  )}
                </Box>
              </Box>
            ))}
            
            {isTyping && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Avatar src={agent.icon} alt={agent.name} sx={{ width: 32, height: 32 }} />
                  <Paper sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {agent.name.split(' ')[0]} is typing...
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={`Message ${agent.name}...`}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                size="small"
              />
              <IconButton onClick={handleSendMessage} disabled={!inputMessage.trim() || isTyping}>
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Quick Actions & Suggestions */}
        <Paper sx={{ width: 300, overflow: 'auto', borderLeft: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">Quick Actions</Typography>
          </Box>
          
          <Box sx={{ p: 2 }}>
            {/* Conversation Starters */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Start a Conversation</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {conversationStarters.map((starter, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setInputMessage(starter);
                      }}
                      sx={{ 
                        textAlign: 'left', 
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontSize: '0.8rem'
                      }}
                    >
                      {starter}
                    </Button>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Work Actions */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Work Actions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TaskIcon />}
                    onClick={() => setInputMessage("Can you give me a status update on your current tasks?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Request Status Update
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GroupIcon />}
                    onClick={() => setInputMessage("Who should we collaborate with on this project?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Discuss Collaboration
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HelpIcon />}
                    onClick={() => setInputMessage("What challenges are you facing? How can I help?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Offer Support
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmojiEventsIcon />}
                    onClick={() => setInputMessage("What achievements are you proud of this week?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Celebrate Wins
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Personal Check-ins */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Personal Check-ins</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MoodIcon />}
                    onClick={() => setInputMessage("How are you feeling today? How's your energy level?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Check Well-being
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CoffeeIcon />}
                    onClick={() => setInputMessage("How's your work-life balance? Any concerns?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Work-Life Balance
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PsychologyIcon />}
                    onClick={() => setInputMessage("What would help you be more productive or satisfied?")}
                    sx={{ textTransform: 'none' }}
                  >
                    Growth Discussion
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Feedback */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Give Feedback</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ThumbUpIcon />}
                    onClick={() => setInputMessage("Great work on the recent project! Your attention to detail really made a difference.")}
                    sx={{ textTransform: 'none', color: 'success.main' }}
                  >
                    Positive Feedback
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ThumbDownIcon />}
                    onClick={() => setInputMessage("I noticed some areas where we could improve. Can we discuss this?")}
                    sx={{ textTransform: 'none', color: 'warning.main' }}
                  >
                    Constructive Feedback
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentChat; 